import { areByteArraysEqual } from "utils/common";
import { sha1 } from "js-sha1";

// CNFGSTRE
export const CSTORE_MAGIC_BYTES = new Uint8Array([ 0x43, 0x4E, 0x46, 0x47, 0x53, 0x54, 0x52, 0x45 ]);
export const CSTORE_SIZE = 2048;
export const CSTORE_HEADER_SIZE = 64;
export const CSTORE_FOOTER_SIZE = 32;
export const TLV_FOOTER_MAGIC = 0x31414853;

export enum TagType {
    unspecified = 0x00,
    bool        = 0x01,
    u32         = 0x02,
    i32         = 0x03,
    f32         = 0x04,
    f64         = 0x05,
    string      = 0x06,
    u8          = 0x07,
    i8          = 0x08,
    u16         = 0x09,
    i16         = 0x0a,
    i64         = 0x0b,
    terminator  = 0xff,
}

export type TagTypeName = keyof typeof TagType;

export type TagDefinition = {
    deprecated: boolean;
    name: string;
    type: TagTypeName;
    units: string;
}

export type TagsToml = {
    tags: {
        definitions: TagDefinition[];
    }
}

export type CodeDefinition = {
    name: string;
    code: number;
}

export type ApplicationDefinition = {
    customer: string;
    apps: CodeDefinition[];
}

export type CodesToml = {
    applications: ApplicationDefinition[];
    customers: CodeDefinition[];
    products: CodeDefinition[];
}

export type HeaderInfo = {
    version: number; // 16 bits
    maxSize: number; // 16 bits, should be CSTORE_SIZE
    buildYYYYMMDD: [number, number, number]; // from 32 bits; YYYY-MM-DD
    productCode: number; // 16 bits
    customerCode: number; // 16 bits
    appCode: number; // 16 bits
    product?: string; // 16 bits; If present, productCode is a recognized code
    customer?: string; // If present, customerCode is a recognized code
    customerApplication?: string; // If present, appCode is a recognized code
    totalEntries: number; // 16 bits
    entriesSize: number; // 16 bits
}

const TAG_HEADER_SIZE = 6;

const dvToUint8Array = ( dv: DataView ): Uint8Array => {
    return new Uint8Array( dv.buffer, dv.byteOffset, dv.byteLength );
};

export const constructStoredTag = (
    tagId: number,
    typ: TagType,
    value: boolean | number | string | bigint | undefined,
): StoredTag => {
    const makeBuf = ( dataLength: number ): DataView => {
        let dv = new DataView(new ArrayBuffer(TAG_HEADER_SIZE + dataLength));
        const tagAndDataType = ( ( typ & 0xff ) << 24 ) | ( tagId & 0xffffff );
        dv.setUint32(0, tagAndDataType, true);
        dv.setUint16(4, dataLength, true);
        return dv;
    };
    let dv: DataView;
    switch ( typ ) {
        case TagType.unspecified:
            throw new BadStoredTagTypeError("<unspecified>", typ, value);
        case TagType.bool:
            if ( typeof value !== "boolean" ) throw new BadStoredTagTypeError("boolean", typ, value);
            dv = makeBuf( 1 );
            dv.setUint8(TAG_HEADER_SIZE, value ? 1 : 0);
            break;
        case TagType.u32:
            if ( typeof value !== "number" ) throw new BadStoredTagTypeError("number", typ, value);
            dv = makeBuf( 4 );
            dv.setUint32(TAG_HEADER_SIZE, value, true);
            break;
        case TagType.i32:
            if ( typeof value !== "number" ) throw new BadStoredTagTypeError("number", typ, value);
            dv = makeBuf( 4 );
            dv.setInt32(TAG_HEADER_SIZE, value, true);
            break;
        case TagType.f32:
            if ( typeof value !== "number" ) throw new BadStoredTagTypeError("number", typ, value);
            dv = makeBuf( 4 );
            dv.setFloat32(TAG_HEADER_SIZE, value, true);
            break;
        case TagType.f64:
            if ( typeof value !== "number" ) throw new BadStoredTagTypeError("number", typ, value);
            dv = makeBuf( 8 );
            dv.setFloat64(TAG_HEADER_SIZE, value, true);
            break;
        case TagType.string:
            {
                if ( typeof value !== "string" ) throw new BadStoredTagTypeError("string", typ, value);
                // ToDo: transform value? No smart quotes, etc.
                let e = new TextEncoder();
                const stringBytesWithoutNUL = e.encode( value );
                for ( const b of stringBytesWithoutNUL ) {
                    if ( b === 0 || b > 0x7f ) {
                        throw new ModifyCStoreError(`Invalid byte ${ b } found in string; must be ASCII`);
                    }
                }
                const dataLength = stringBytesWithoutNUL.length + 1; // +1 for NUL byte
                dv = makeBuf( dataLength );
                let tmp = dvToUint8Array( dv );
                tmp.set( stringBytesWithoutNUL, TAG_HEADER_SIZE ); // copy into dv
                dv.setInt8(TAG_HEADER_SIZE + dataLength - 1, 0); // NUL-terminated
            }
            break;
        case TagType.u8:
            if ( typeof value !== "number" ) throw new BadStoredTagTypeError("number", typ, value);
            dv = makeBuf( 1 );
            dv.setUint8(TAG_HEADER_SIZE, value);
            break;
        case TagType.i8:
            if ( typeof value !== "number" ) throw new BadStoredTagTypeError("number", typ, value);
            dv = makeBuf( 1 );
            dv.setInt8(TAG_HEADER_SIZE, value);
            break;
        case TagType.u16:
            if ( typeof value !== "number" ) throw new BadStoredTagTypeError("number", typ, value);
            dv = makeBuf( 2 );
            dv.setUint16(TAG_HEADER_SIZE, value, true);
            break;
        case TagType.i16:
            if ( typeof value !== "number" ) throw new BadStoredTagTypeError("number", typ, value);
            dv = makeBuf( 2 );
            dv.setInt16(TAG_HEADER_SIZE, value, true);
            break;
        case TagType.i64:
            if ( typeof value !== "bigint" ) throw new BadStoredTagTypeError("bigint", typ, value);
            dv = makeBuf( 8 );
            dv.setBigInt64(TAG_HEADER_SIZE, value, true);
            break;
        case TagType.terminator:
            if ( typeof value !== "undefined" ) throw new BadStoredTagTypeError("undefined", typ, value);
            if ( tagId !== 0xffffff ) {
                throw new ModifyCStoreError("encoded terminator data type without terminator tag ID");
            }
            dv = makeBuf( 0 );
            break;
        default:
            throw new ModifyCStoreError(`invalid dataType ${ typ }`);
    }
    return new StoredTag( dv, dv.byteOffset );
}

export class StoredTag {
    readonly dataType: TagType; // 8 bits
    readonly tagId: number; // 24 bits
    readonly length: number; // actual data length; 6 less than dv.length
    readonly dv: DataView; // complete tag data including 32-bit tag and length; offset 6 holds actual data
    readonly baseOffset: number; // offset of start of cstore header in underlying buffer
    private memoizedTagDef: TagDefinition | undefined;

    constructor( dv: DataView, baseOffset?: number ) {
        const tagAndDataType = dv.getUint32(0, true);
        this.dataType = tagAndDataType >> 24;
        this.tagId = tagAndDataType & 0xffffff;
        this.length = dv.getUint16(4, true);
        this.dv = dv;
        this.baseOffset = baseOffset ?? 0;
    }

    isValid(): boolean {
        return this.isTerminator() || ( TagType[this.dataType] !== undefined && this.dataType !== TagType.unspecified );
    }

    isTerminator(): boolean {
        // The first condition is due to some odd implementation detail of JS
        return this.dataType as number === -1 || this.dataType === TagType.terminator;
    }

    hasData(): boolean {
        return this.isValid() && !this.isTerminator();
    }

    // convert from tagId using tags.toml
    getTagDefinition(allTags: TagsToml): TagDefinition | undefined {
        if ( this.memoizedTagDef ) return this.memoizedTagDef;
        return ( this.memoizedTagDef = allTags.tags.definitions[this.tagId] );
    }

    // returns undefined for terminator
    getData(): boolean | number | string | bigint | undefined {
        const o = this.dv.byteOffset;
        const oStr = (): string =>
            '0x' + (o - this.baseOffset).toString(16).padStart(3, '0'); // For logging
        switch (this.dataType) {
            case TagType.unspecified:
                throw new BadTagError(`tag at offset ${oStr()} is "unspecified" data type`);
            case TagType.bool:
                {
                    const b = this.dv.getUint8(TAG_HEADER_SIZE);
                    if ( b > 1 ) throw new BadTagError(`tag at offset ${oStr()} has invalid bool byte ${b}`);
                    return b === 1;
                }
            case TagType.u32:
                return this.dv.getUint32(TAG_HEADER_SIZE, true);
            case TagType.i32:
                return this.dv.getInt32(TAG_HEADER_SIZE, true);
            case TagType.f32:
                return this.dv.getFloat32(TAG_HEADER_SIZE, true);
            case TagType.f64:
                return this.dv.getFloat64(TAG_HEADER_SIZE, true);
            case TagType.string:
                {
                    const bytes = new Uint8Array(this.dv.buffer, o + TAG_HEADER_SIZE, this.length);
                    if ( bytes[this.length - 1] !== 0 ) throw new BadTagError(`tag at offset ${oStr()} has non-zero-terminated string`);
                    for ( let i = 0; i < this.length - 1; i++ ) {
                        if ( bytes[i] & 0x80 ) throw new BadTagError(`tag at offset 0x${oStr()} has non-ASCII string`);
                    }
                    let td = new TextDecoder();
                    return td.decode(new Uint8Array(bytes.buffer, bytes.byteOffset, this.length - 1)); // Decode without NUL byte at end
                }
            case TagType.u8:
                return this.dv.getUint8(TAG_HEADER_SIZE);
            case TagType.i8:
                return this.dv.getInt8(TAG_HEADER_SIZE);
            case TagType.u16:
                return this.dv.getUint16(TAG_HEADER_SIZE, true);
            case TagType.i16:
                return this.dv.getInt16(TAG_HEADER_SIZE, true);
            case TagType.i64:
                return this.dv.getBigInt64(TAG_HEADER_SIZE, true);
            case TagType.terminator:
                return;
            default:
                throw new BadTagError(`tag at offset ${oStr()} has unknown data type ${this.dataType}`);
        }
    }
}


const decompose = (bin: Uint8Array): {hdr: DataView, tags: StoredTag[], footer: DataView} => {
    if ( bin.length !== CSTORE_SIZE ) throw new LoadCStoreError(`bad cstore size: ${bin.length}`);
    const hdr = new DataView(bin.buffer, bin.byteOffset, CSTORE_HEADER_SIZE);
    // Check magic to avoid cryptic error in for loop when there is garbage
    const cstoreMagicDV = new Uint8Array( hdr.buffer, hdr.byteOffset, CSTORE_MAGIC_BYTES.length );
    if ( ! areByteArraysEqual( cstoreMagicDV, CSTORE_MAGIC_BYTES ) ) {
        throw new LoadCStoreError(`invalid magic ${ cstoreMagicDV.join(',') }`);
    }

    // The above checks length of magic, below we are checking if the SHA1 and the head / boots were correctly read.
    const footer = new DataView(bin.buffer, bin.byteOffset + CSTORE_SIZE - CSTORE_FOOTER_SIZE, CSTORE_FOOTER_SIZE);
    
    const SHA1_IN_FOOTER = new Uint8Array( footer.buffer, footer.byteOffset + 4, 20 );

    const dataToHash = new Uint8Array( bin.buffer, bin.byteOffset, CSTORE_SIZE - CSTORE_FOOTER_SIZE );
    const calculatedHash = new Uint8Array( sha1.arrayBuffer( dataToHash ) );

    if ( !areByteArraysEqual( SHA1_IN_FOOTER, calculatedHash ) ) {
        throw new LoadCStoreError("incorrect SHA1 in footer");
    }
  
    if( footer.getUint32(0, true) !== TLV_FOOTER_MAGIC ) {
        throw new LoadCStoreError("incorrect footer magic hat");
    }
  
    if ( footer.getUint32(24, true) !== TLV_FOOTER_MAGIC ) {
        throw new LoadCStoreError("incorrect footer magic boots");
    }
    
    let tags: StoredTag[] = [];
    const endOffset = CSTORE_SIZE - CSTORE_FOOTER_SIZE;
    let foundTerminator = false;

    for ( let offset = CSTORE_HEADER_SIZE ; ; ) {
        if ( offset > endOffset ) throw new LoadCStoreError("tag overrun into footer");
        const tagDV = new DataView(bin.buffer, bin.byteOffset + offset); // Warning: goes to end!!!! Not OK for StoredTag constructor
        const tagAndDataType = tagDV.getUint32(0, true);
        const length = tagDV.getUint16(4, true);
        if ( tagAndDataType === 0xffffffff && length === 0 ) {
            foundTerminator = true;
        }
        let dv;
        try {
            dv = new DataView(bin.buffer, bin.byteOffset + offset, TAG_HEADER_SIZE + length);
        } catch (e) {
            throw new LoadCStoreError(`bad DataView constructed for tag at offset 0x${offset.toString(16).padStart(2,'0')}: ${ e }`);
        }
        const tag = new StoredTag(dv, bin.byteOffset);
        if ( !tag.isValid() ) throw new LoadCStoreError(`invalid tag at offset 0x${offset.toString(16).padStart(2,'0')}`);
        tags.push(tag);
        offset += TAG_HEADER_SIZE + length;
        if ( foundTerminator ) break;
    }

    if ( !foundTerminator ) throw new LoadCStoreError("no terminator");
    return {hdr, tags, footer};
}

const decodeHeaderBytes = ( headerBytes: DataView, codes: CodesToml ): HeaderInfo => {
    const cstoreMagicDV = new Uint8Array( headerBytes.buffer, headerBytes.byteOffset, CSTORE_MAGIC_BYTES.length );
    if ( ! areByteArraysEqual( cstoreMagicDV, CSTORE_MAGIC_BYTES ) ) {
        throw new LoadCStoreError("invalid magic");
    }

    // 0x08: Configuration Store version (16 bits).
    const version = headerBytes.getUint16(0x08, true);
    // 0x0a: Maximum size of the config store (16 bits, current file size is planned to be 2048 bytes).
    const maxSize = headerBytes.getUint16(0x0a, true);
    // 0x0c: Build date and time (32 bits).
    //   Day = bits [4:0] 5 bits (supports values 1-31), 1's based number.
    //   Month = bits [8:5] 4 bits (supports valid values 1-12), 1's based number.
    //   Year = bits [20:9] 12 bits (supports values 0-4095), 1's based number.
    //   Reserved = bits [31:21].
    const buildDate = headerBytes.getUint32(0x0c, true);
    const buildYYYYMMDD: [number, number, number] = [
        ( buildDate >> 9 ) & 0xfff, // YYYY
        ( buildDate >> 5 ) & 0xf,   // MM
        buildDate & 0x1f, // DD
    ];
    // 0x10: Product Code (16-bit).
    let product: string | undefined;
    const productCode = headerBytes.getUint16(0x10, true);
    for ( const codeDef of codes.products ) {
        if ( codeDef.code === productCode ) {
            // Recognized code
            product = codeDef.name;
            break;
        }
    }
    // 0x12: Customer Code (16-bit).
    let customer: string | undefined;
    const customerCode = headerBytes.getUint16(0x12, true);
    for ( const codeDef of codes.customers ) {
        if ( codeDef.code === customerCode ) {
            // Recognized code
            customer = codeDef.name;
            break;
        }
    }
    // 0x14: Customer Application Code (16-bit).
    let customerApplication: string | undefined;
    const appCode = headerBytes.getUint16(0x14, true);
    for ( const appDef of codes.applications ) {
        if ( customer === undefined ) break;
        if ( appDef.customer === customer ) {
            for ( const codeDef of appDef.apps ) {
                if ( codeDef.code === appCode ) {
                    // Recognized code
                    customerApplication = codeDef.name;
                    break;
                }
            }
            break;
        }
    }
    // 0x16: Number of total entries in the data payload (16 bits)
    const totalEntries = headerBytes.getUint16(0x16, true);
    // 0x18: Size of total entries in the data payload (excluding SHA-1) (16 bits)
    const entriesSize = headerBytes.getUint16(0x18, true);
    return {
        version, maxSize, buildYYYYMMDD, productCode, customerCode, appCode, product, customer,
        customerApplication, totalEntries, entriesSize,
    }
};

const encodeHeaderBytes = ( headerInfo: HeaderInfo ): DataView => {
    let dv = new DataView( new ArrayBuffer(64) );
    for ( let i = 0; i < CSTORE_MAGIC_BYTES.length; i++ ) {
        dv.setUint8(i, CSTORE_MAGIC_BYTES[i]);
    }
    dv.setUint16(0x08, headerInfo.version, true);
    dv.setUint16(0x0a, headerInfo.maxSize, true);
    const [ YYYY, MM, DD ] = headerInfo.buildYYYYMMDD;
    if ( MM < 1 || MM > 12 || DD < 1 || DD > 31 || YYYY < 0 || YYYY > 4095 ) {
        throw new ModifyCStoreError( `invalid buildYYYYMMDD: ${ headerInfo.buildYYYYMMDD }` );
    }
    const buildDate = ( YYYY << 9 ) | ( MM << 5 ) | DD;
    dv.setUint32(0x0c, buildDate, true);
    dv.setUint16(0x10, headerInfo.productCode, true);
    dv.setUint16(0x12, headerInfo.customerCode, true);
    dv.setUint16(0x14, headerInfo.appCode, true);
    dv.setUint16(0x16, headerInfo.totalEntries, true);
    dv.setUint16(0x18, headerInfo.entriesSize, true);
    return dv;
};

const calcHash = ( headerBytes: DataView, storedTags: StoredTag[] ): Uint8Array => {
    let h = sha1.create();
    let remainingBytes = CSTORE_SIZE - CSTORE_FOOTER_SIZE;
    let a = dvToUint8Array( headerBytes );
    h.update(a); remainingBytes -= a.length;
    for ( const tag of storedTags ) {
        a = dvToUint8Array( tag.dv );
        h.update(a); remainingBytes -= a.length;
    }
    if ( remainingBytes < 0 ) {
        // Unreachable when storedTags is constructed the usual way
        throw new CStoreError("too much data being hashed");
    } else if ( remainingBytes > 0 ) {
       h.update( new Uint8Array( remainingBytes ) ); // These bytes are all zeroes
    }
    return new Uint8Array( h.arrayBuffer() );
};

export enum OpType {
    UpdateTag,
    AddTag,
    UpsertTag,
    UpdateHeader,
}

export type Op = {
    type: OpType.UpdateTag | OpType.AddTag | OpType.UpsertTag;
    id: string | number;
    value: boolean | number | string | bigint;
} | {
    type: OpType.UpdateHeader;
    // really the only thing worth modifying is buildYYYYMMDD
    updates: Partial<HeaderInfo>;
};

export const READ_ONLY_HEADER_FIELDS: (keyof HeaderInfo)[] =
    [ "productCode", "customerCode", "appCode", "product", "customer", "customerApplication",
        "totalEntries", "entriesSize" ];

// Immutable configuration store. Use .modify to get a new CStore based off of this one, but with modifications.
export class CStore {
    readonly allTags: TagsToml;
    readonly codes: CodesToml;
    readonly headerBytes: DataView;
    readonly header: HeaderInfo;
    readonly storedTags: StoredTag[]; // includes terminator
    readonly footerBytes: DataView;

    constructor(allTags: TagsToml, codes: CodesToml, binOrParts: Uint8Array | DataView[], verifyHash: boolean = true) {
        this.allTags = allTags;
        this.codes = codes;

        if ( binOrParts instanceof Uint8Array ) {
            let { hdr, tags, footer } = decompose( binOrParts );
            this.headerBytes = hdr;
            this.storedTags = tags;
            this.footerBytes = footer;
        } else {
            const parts = binOrParts;
            if ( parts.length < 2 ) {
                throw new LoadCStoreError("bad parts: should be least 2 parts, was " + parts.length);
            }
            this.headerBytes = parts[0];
            if ( this.headerBytes.byteLength !== CSTORE_HEADER_SIZE ) {
                throw new LoadCStoreError("bad header length: " + this.headerBytes.byteLength);
            }
            this.storedTags = parts.slice(1, -1).map( ( dv: DataView ) => new StoredTag(dv))
            this.footerBytes = parts[parts.length-1];
            if ( this.footerBytes.byteLength !== CSTORE_FOOTER_SIZE ) {
                throw new LoadCStoreError("bad footer length: " + this.footerBytes.byteLength);
            }
        }
        this.header = decodeHeaderBytes( this.headerBytes, codes );
        this.verifyFooter(verifyHash);
    }

    // calculate the maximum size of the StoredTag that could be added (including both TAG_HEADER_SIZE and data)
    getRemainingSpace(): number {
        return CSTORE_SIZE - CSTORE_HEADER_SIZE - CSTORE_FOOTER_SIZE - this.header.entriesSize;
    }

    calcHash(): Uint8Array {
        return calcHash( this.headerBytes, this.storedTags );
    }

    verifyFooter(verifyHash: boolean) {
        if ( this.footerBytes.getUint32(0, true) !== TLV_FOOTER_MAGIC ) {
            throw new CStoreError("incorrect footer magic hat");
        }
        if ( this.footerBytes.getUint32(24, true) !== TLV_FOOTER_MAGIC ) {
            throw new CStoreError("incorrect footer magic boots");
        }
        if ( verifyHash ) {
            const calculated = this.calcHash();
            // extract the SHA1 at offset 4 (size 20)
            const extracted = new Uint8Array( this.footerBytes.buffer, this.footerBytes.byteOffset + 4, 20 );
            if ( !areByteArraysEqual( calculated, extracted ) ) {
                throw new CStoreError("incorrect SHA1 in footer");
            }
        }
    }

    // Returns a new CStore with the specified sequence of `Op` operations applied to this CStore.
    modify(ops: Op[]): CStore {
        if ( ops.length === 0 ) return this;

        let newTags: StoredTag[] | undefined;
        let newHeader = this.header;
        for ( const op of ops ) {
            let matchingIndices = []; // indices in newTags of whichever has matching tag ID
            if ( op.type === OpType.UpdateTag || op.type === OpType.AddTag || op.type === OpType.UpsertTag ) {
                newTags ??= this.storedTags.filter( ( tag: StoredTag ) => !tag.isTerminator());

                let tagId: number | undefined;
                let tagDef: TagDefinition | undefined;
                for ( let i = 0; i < this.allTags.tags.definitions.length; i++ ) {
                    const def = this.allTags.tags.definitions[i];
                    if ( typeof op.id === "string" && op.id === def.name ) {
                        tagId = i;
                        tagDef = def;
                        break;
                    }
                    if ( typeof op.id === "number" && op.id === i ) {
                        tagId = i;
                        tagDef = def;
                        break;
                    }
                }
                if ( tagId === undefined || tagDef === undefined ) {
                    throw new ModifyCStoreError(`Op has unknown tag ${ op.id }`);
                }
                if ( tagDef.type === "terminator" || tagDef.type === "unspecified" ) {
                    throw new ModifyCStoreError( `Op uses invalid tag definition (type is ${ tagDef.type })` );
                }
                if ( tagDef.deprecated ) {
                    console.warn( `Deprecated tag ${ op.id }` );
                }
                for ( let i = 0; i < newTags.length; i++ ) {
                    const def = newTags[i].getTagDefinition( this.allTags );
                    if ( def === tagDef ) matchingIndices.push(i);
                }

                let idx: number | undefined;
                const l = matchingIndices.length;
                switch ( op.type ) {
                    case OpType.UpdateTag:
                        if ( matchingIndices.length !== 1 ) {
                            throw new ModifyCStoreError(`UpdateTag but wrong number of matching tags already there: ${ l }; exactly one expected` );
                        }
                        idx = matchingIndices[0];
                        break;
                    case OpType.AddTag:
                        if ( matchingIndices.length ) {
                            throw new ModifyCStoreError(`AddTag but wrong number of matching tags already there: ${ l }; none expected` );
                        }
                        idx = newTags.length;
                        break;
                    case OpType.UpsertTag:
                        if ( matchingIndices.length > 1 ) {
                            throw new ModifyCStoreError(`UpsertTag but wrong number of matching tags already there: ${ l }; at most one expected` );
                        }
                        idx = matchingIndices.length ? matchingIndices[0] : newTags.length;
                        break;
                }
                const typ = TagType[tagDef.type];
                const newTag = constructStoredTag( tagId, typ, op.value );
                newTags[idx] = newTag;
            } else if ( op.type === OpType.UpdateHeader ) {
                // Check for disallowed
                for ( const headerField in op.updates ) {
                    if ( READ_ONLY_HEADER_FIELDS.includes( headerField as keyof HeaderInfo ) ) {
                        throw new ModifyCStoreError( `cannot update header field ${ headerField }!` );
                    }
                }
                newHeader = { ...newHeader, ...op.updates };
            } else {
                throw new ModifyCStoreError(`Unexpected modify Op type: ${ op.type }`);
            }
        }

        if ( newTags !== undefined ) {
            // Tags were modified.
            // re-add terminator now that we're done
            newTags?.push( constructStoredTag( 0xffffff, TagType.terminator, undefined ) );

            // update totalEntries and entriesSize if needed
            const totalEntries = newTags.length;
            const entriesSize = newTags.reduce( ( total: number, tag: StoredTag ) => total + tag.dv.byteLength, 0 );
            if ( newHeader.totalEntries !== totalEntries || newHeader.entriesSize !== entriesSize ) {
                newHeader = { ...newHeader, totalEntries, entriesSize };
            }
        }
        let [ headerBytes, footerBytes ] = [ this.headerBytes, this.footerBytes ];
        if ( newHeader !== this.header || newTags !== undefined ) {
            // Anything was modified; SHA-1 needs recalculation.
            headerBytes = encodeHeaderBytes(newHeader);
            const newHash = calcHash( headerBytes, newTags ?? this.storedTags );
            footerBytes = new DataView( new ArrayBuffer( CSTORE_FOOTER_SIZE ) );
            footerBytes.setUint32(0, TLV_FOOTER_MAGIC, true);
            if ( newHash.length !== 20 ) throw new ModifyCStoreError( `unexpected newHash length` );
            for ( let i = 0; i < newHash.length; i++ ) {
                footerBytes.setUint8( 4 + i, newHash[i] );
            }
            footerBytes.setUint32(24, TLV_FOOTER_MAGIC, true);
        }

        const storedTagDVs = ( newTags ?? this.storedTags ).map( ( tag ) => tag.dv );
        return new CStore( this.allTags, this.codes, [ headerBytes, ...storedTagDVs, footerBytes ] );
    }

    export(): Uint8Array {
        let bin = new Uint8Array(CSTORE_SIZE);
        const hdr = dvToUint8Array( this.headerBytes );
        bin.set( hdr, 0 );
        let offset = CSTORE_HEADER_SIZE;
        for ( const tag of this.storedTags ) {
            const tagBytes = dvToUint8Array( tag.dv );
            bin.set( tagBytes, offset );
            offset += tagBytes.length;
        }
        const footerOffset = CSTORE_SIZE - CSTORE_FOOTER_SIZE;
        if ( offset > footerOffset ) {
            throw new CStoreError(`exporting bytes of cstore with too many tag bytes! offset is { offset }`);
        }
        // readonly storedTags: StoredTag[]; // includes terminator
        const footer = dvToUint8Array( this.footerBytes );
        bin.set( footer, footerOffset );
        return bin;
    }
}

export class CStoreError extends Error {
    constructor(m: string) {
        super(m);

        // Set the prototype explicitly.
        Object.setPrototypeOf(this, CStoreError.prototype);
    }
}

export class ModifyCStoreError extends CStoreError {
    constructor(m: string) {
        super(m);

        // Set the prototype explicitly.
        Object.setPrototypeOf(this, ModifyCStoreError.prototype);
    }
}

export class LoadCStoreError extends CStoreError {
    constructor(m: string) {
        super(m);

        // Set the prototype explicitly.
        Object.setPrototypeOf(this, LoadCStoreError.prototype);
    }
}

export class BadTagError extends CStoreError {
    constructor(m: string) {
        super(m);

        // Set the prototype explicitly.
        Object.setPrototypeOf(this, BadTagError.prototype);
    }
}

export class BadStoredTagTypeError extends CStoreError {
    readonly expectedType: string;
    readonly value: boolean | number | string | bigint | undefined;
    readonly dataType: TagType;

    constructor(expectedType: string, dataType: TagType, value: boolean | number | string | bigint | undefined) {
        const tagTypeStr = '0x' + dataType.toString(16).padStart(2, '0');
        const errStr = `when encoding StoredTag, expected JS type ${ expectedType }, but actual type ` +
            `is ${ typeof value } (for tag type ${tagTypeStr})`;
        super(errStr);

        this.expectedType = expectedType;
        this.dataType = dataType;
        this.value = value;

        // Set the prototype explicitly.
        Object.setPrototypeOf(this, BadTagError.prototype);
    }
}
